import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ClipboardList, FileText, PackageCheck, User } from 'lucide-react';
import type { SurveyDto } from '@eombor/contracts';
import { Badge, Button, Card, EmptyState, Skeleton, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { accountingApi } from '../../accounting/api/accounting.api.js';
import { useReadyHandovers, type ReadyHandoverRow } from '../../handover/model/handover.queries.js';
import { useNomenclatures } from '../../catalog/model/catalog.queries.js';

/**
 * «MENING ISHIM» — panelning eng tepasi.
 *
 * Boshqaruv panelida ilgari faqat TAHLIL turardi va u butun mintaqaning
 * yig'ma raqamlari edi: rahbarga kerakli, skladchiga esa umuman begona.
 * Smena boshida ekranni ochgan skladchi «bugun nima qilaman?» degan
 * savoliga javob topmasdi — javob boshqa bo'limda, «Berish» sahifasida
 * yotardi va u yerga borish kerakligini bilish kerak edi.
 *
 * Endi eng tepada ROLGA QARAB o'z ishi turadi. Bo'lim RUXSATdan
 * quriladi, lavozimdan emas: bitta odam ham skladchi, ham buxgalter
 * bo'lishi mumkin va u holda ikkala blok ham chiziladi.
 *
 * Ruxsati yo'q bo'lsa — bo'lim UMUMAN chizilmaydi. Bo'sh sarlavha
 * «bu yerda nimadir bo'lishi kerak edi» degan taassurot qoldirardi.
 */

/** Nechta satr kartochkada ko'rinadi — qolgani sanoq bo'lib qisqaradi. */
const SHOWN = 4;

/** `2026-09-12T…` dan bugungacha necha kun. Noto'g'ri sana — `null`. */
const daysSince = (iso: string): number | null => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - at.getTime()) / 86_400_000));
};

/**
 * Kutish MUDDATI qachon ogohlantirishga aylanadi — voronkadagi AYNI
 * chegaralar (`PipelineBlock`). Ikki joyda boshqa son bo'lsa, bitta
 * hujjat bir ekranda sariq, boshqasida oq bo'lib ko'rinardi.
 */
const AGE_WARN_DAYS = 7;

const Metric = ({ label, value, tone }: { label: string; value: string; tone?: 'warn' }) => (
  <span className="flex min-w-0 flex-col gap-0.5">
    <span className="text-xs tracking-wide text-text-muted uppercase">{label}</span>
    <span
      className={cn(
        'tabular text-2xl leading-7 font-semibold',
        tone === 'warn' ? 'text-warn' : 'text-text',
      )}
    >
      {value}
    </span>
  </span>
);

/**
 * SKLADCHINING ishi — berishga tayyor talabnomalar.
 *
 * Ro'yxat qog'ozdagi «ТРЕБОВАНИЕ №» bilan boshlanadi: olib ketayotgan
 * odamning qo'lida aynan shu raqamli qog'oz turadi va skladchining
 * birinchi ishi ikkovini solishtirish.
 */
const ReadyToIssue = ({ onOpenHandover }: { onOpenHandover?: () => void }) => {
  const ready = useReadyHandovers();
  const nomenclatures = useNomenclatures({ limit: 200 });

  const rows = ready.rows;
  const lines = rows.reduce((sum, row) => sum + row.lines.length, 0);

  /* Eng uzoq kutgani — «uchta hujjat turibdi» odatiy hol, ulardan biri
     o'n kundan beri turgan bo'lsa esa bu allaqachon muammo. */
  const oldest = rows.reduce<number>((max, row) => {
    const days = daysSince(row.requisition.createdAt);
    return days === null ? max : Math.max(max, days);
  }, 0);

  const nameOf = (id: string): string =>
    nomenclatures.data?.items.find((item) => item.id === id)?.name ?? `#${id}`;

  const unitOf = (id: string): string => {
    const found = nomenclatures.data?.items.find((item) => item.id === id);
    return found === undefined ? '' : unitText(found.unit);
  };

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <header className="flex flex-wrap items-center gap-3">
          {/* Rangni IKONKA kvadrati olib yuradi. Kartochkaning chap
              chekkasiga yo'g'on rangli chiziq ham qo'yilgan edi — o'sha
              bitta ma'noni ikki marta aytardi va ro'yxatda o'nta
              kartochka turganda ular bosma chiziqlar bo'lib ko'rinardi. */}
          <span
            aria-hidden
            className="bg-accent-soft text-accent flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-md)]"
          >
            <PackageCheck size={20} />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <h2 className="text-base font-semibold text-text">{t('mywork.issueTitle')}</h2>
            <p className="text-sm text-text-muted">{t('mywork.issueHint')}</p>
          </span>

          {onOpenHandover === undefined ? null : (
            <Button variant="secondary" size="sm" onClick={onOpenHandover}>
              {t('mywork.issueOpen')}
              <ArrowRight size={15} aria-hidden />
            </Button>
          )}
        </header>

        {ready.isPending ? (
          <Skeleton lines={4} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<PackageCheck size={20} aria-hidden />}
            title={t('mywork.issueEmpty')}
            description={t('mywork.issueEmptyHint')}
          />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Metric label={t('mywork.issueCount')} value={String(rows.length)} />
              <Metric label={t('mywork.issueLines')} value={String(lines)} />
              <Metric
                label={t('mywork.issueOldest')}
                value={t('mywork.days', { days: oldest })}
                {...(oldest >= AGE_WARN_DAYS ? { tone: 'warn' as const } : {})}
              />
            </div>

            <ul className="grid gap-2 lg:grid-cols-2">
              {rows.slice(0, SHOWN).map((row: ReadyHandoverRow) => {
                const days = daysSince(row.requisition.createdAt) ?? 0;
                return (
                  <li
                    key={row.requisition.id}
                    className="border-border flex min-w-0 flex-col gap-1.5 rounded-[var(--radius-md)] border p-3"
                  >
                    <span className="flex items-center gap-2">
                      <FileText size={14} aria-hidden className="text-text-faint shrink-0" />
                      <span className="tabular truncate text-sm font-semibold text-text">
                        {row.formNo ?? row.requisition.docNo}
                      </span>
                      <Badge
                        tone={days >= AGE_WARN_DAYS ? 'warn' : 'neutral'}
                        className="ml-auto shrink-0"
                      >
                        {t('mywork.days', { days })}
                      </Badge>
                    </span>

                    {row.requisition.throughPerson === null ? null : (
                      <span className="flex items-center gap-1.5 text-xs text-text-muted">
                        <User size={12} aria-hidden className="shrink-0" />
                        <span className="truncate">{row.requisition.throughPerson}</span>
                      </span>
                    )}

                    <span className="truncate text-xs text-text">
                      {row.lines
                        .slice(0, 2)
                        .map(
                          (line) =>
                            `${nameOf(line.nomenclatureId)} · ${String(line.qty)} ${unitOf(line.nomenclatureId)}`,
                        )
                        .join(' · ')}
                      {row.lines.length > 2
                        ? ` ${t('mywork.more', { count: row.lines.length - 2 })}`
                        : ''}
                    </span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </Card>
  );
};

/**
 * BUXGALTERNING ishi — tasdiq kutayotgan so'rovnomalar.
 *
 * Zanjir aynan shu yerda to'xtaydi: so'rovnoma tasdiqlanmaguncha
 * mahsulot berilmaydi va talabnoma bandlikda turaveradi. Ya'ni
 * buxgalterning kechikishi OMBORDA ko'rinadi, uning o'z ekranida emas —
 * shuning uchun son smena boshidagi ekranga chiqariladi.
 */
const PendingSurveys = ({ onOpenSurveys }: { onOpenSurveys?: () => void }) => {
  const surveys = useQuery({
    queryKey: ['accounting', 'surveys', 'pending', 'dashboard'],
    queryFn: () => accountingApi.listSurveys({ status: 'PENDING', page: 1, limit: 20 }),
    staleTime: 60_000,
  });

  const rows: SurveyDto[] = surveys.data?.items ?? [];
  const oldest = rows.reduce<number>((max, row) => {
    const days = daysSince(row.createdAt);
    return days === null ? max : Math.max(max, days);
  }, 0);

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <header className="flex flex-wrap items-center gap-3">
          <span
            aria-hidden
            className="bg-warn-soft text-warn flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-md)]"
          >
            <ClipboardList size={20} />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <h2 className="text-base font-semibold text-text">{t('mywork.surveyTitle')}</h2>
            <p className="text-sm text-text-muted">{t('mywork.surveyHint')}</p>
          </span>

          {onOpenSurveys === undefined ? null : (
            <Button variant="secondary" size="sm" onClick={onOpenSurveys}>
              {t('mywork.surveyOpen')}
              <ArrowRight size={15} aria-hidden />
            </Button>
          )}
        </header>

        {surveys.isPending ? (
          <Skeleton lines={4} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<ClipboardList size={20} aria-hidden />}
            title={t('mywork.surveyEmpty')}
            description={t('mywork.surveyEmptyHint')}
          />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Metric label={t('mywork.surveyCount')} value={String(rows.length)} />
              <Metric
                label={t('mywork.surveyOldest')}
                value={t('mywork.days', { days: oldest })}
                {...(oldest >= AGE_WARN_DAYS ? { tone: 'warn' as const } : {})}
              />
            </div>

            <ul className="grid gap-2 lg:grid-cols-2">
              {rows.slice(0, SHOWN).map((row) => {
                const days = daysSince(row.createdAt) ?? 0;
                return (
                  <li
                    key={row.id}
                    className="border-border flex min-w-0 flex-col gap-1.5 rounded-[var(--radius-md)] border p-3"
                  >
                    <span className="flex items-center gap-2">
                      <FileText size={14} aria-hidden className="text-text-faint shrink-0" />
                      <span className="tabular truncate text-sm font-semibold text-text">
                        {row.formNo ?? row.docNo}
                      </span>
                      <Badge
                        tone={days >= AGE_WARN_DAYS ? 'warn' : 'neutral'}
                        className="ml-auto shrink-0"
                      >
                        {t('mywork.days', { days })}
                      </Badge>
                    </span>

                    <span className="tabular text-xs text-text-faint">{row.requisitionDocNo}</span>

                    {row.purpose === null || row.purpose === '' ? null : (
                      <span className="line-clamp-2 text-xs text-text">{row.purpose}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </Card>
  );
};

export interface MyWorkSectionProps {
  /** «Berish» sahifasiga o'tish — bo'lim marshrutni bilmaydi. */
  onOpenHandover?: () => void;
  /** Buxgalteriya bo'limiga o'tish. */
  onOpenSurveys?: () => void;
}

export const MyWorkSection = ({ onOpenHandover, onOpenSurveys }: MyWorkSectionProps = {}) => {
  const { canAnywhere } = usePermission();

  const issues = canAnywhere('stock.issue');
  const surveys = canAnywhere('accounting.survey.approve');

  if (!issues && !surveys) return null;

  return (
    <section className="flex flex-col gap-4">
      {issues ? (
        <ReadyToIssue {...(onOpenHandover === undefined ? {} : { onOpenHandover })} />
      ) : null}
      {surveys ? (
        <PendingSurveys {...(onOpenSurveys === undefined ? {} : { onOpenSurveys })} />
      ) : null}
    </section>
  );
};
